// src/components/charts/core/ChartError.tsx
/**
 * ChartError - Error state for charts
 * 
 * Displays a user-friendly error message when chart loading fails.
 */

import React from "react";
import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ChartErrorProps {
  /** Chart title */
  title: string;
  /** Error message to display */
  error: string;
  /** Optional retry handler */
  onRetry?: () => void;
  /** Disable animation */
  disableAnimation?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const ChartError: React.FC<ChartErrorProps> = ({
  title,
  error,
  onRetry,
  disableAnimation = false,
}) => {
  const Wrapper = disableAnimation ? "div" : motion.div;
  const wrapperProps = disableAnimation ? {} : {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <Wrapper
      {...wrapperProps}
      className="relative rounded-2xl overflow-hidden my-5 p-5"
      style={{
        background: "linear-gradient(135deg, rgba(27,17,17,0.8) 0%, rgba(36,24,24,0.6) 100%)",
        border: "1px solid rgba(239, 68, 68, 0.2)",
      }}
    >
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-lg bg-rose-500/20 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="h-4 w-4 text-rose-400" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-white/80">{title}</h4>
          <p className="text-xs text-rose-300/70 mt-1">{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-3 text-xs text-rose-400 hover:text-rose-300 underline underline-offset-2 transition-colors"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default ChartError;

